import { API_ENDPOINTS } from '../constants';

import { get, post, put, del } from './apiClient';

// Pagination types
export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  next_page: number | null;
  prev_page: number | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// District types based on backend schemas
export interface District {
  id: number;
  name: string;
  code: string;
  state: string;
  country: string;
  city?: string;
  zip_code?: string;
  superintendent?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  created_at: string;
  updated_at: string;
}

export interface DistrictCreate {
  name: string;
  code: string;
  state: string;
  country: string;
  city?: string;
  zip_code?: string;
  superintendent?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
}

export interface DistrictUpdate {
  name?: string;
  code?: string;
  state?: string;
  country?: string;
  city?: string;
  zip_code?: string;
  superintendent?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
}

export interface DataSharingConsent {
  district_id: number;
  consent: boolean;
  agreement_version: string | null;
  signed_at: string | null;
  revoked_at: string | null;
  updated_by_user_id: number | null;
}

export interface DataSharingConsentUpdate {
  consent: boolean;
  agreement_version?: string;
}

export interface DistrictDetail extends District {
  schools: School[];
  user_count?: number;
  school_count?: number;
}

export interface School {
  id: number;
  name: string;
  code: string;
  school_type: string;
  grade_levels: string[];
  district_id: number;
  created_at: string;
  updated_at: string;
}

export interface DistrictsListParams {
  skip?: number;
  limit?: number;
  search?: string;
}

export type DistrictsResponse = PaginatedResponse<District>

// API functions
export const getDistricts = async (params: DistrictsListParams = {}): Promise<DistrictsResponse> => {
  const searchParams = new URLSearchParams();
  
  if (params.skip !== undefined) searchParams.append('skip', params.skip.toString());
  if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
  if (params.search) searchParams.append('search', params.search);
  
  const queryString = searchParams.toString();
  const endpoint = queryString ? `${API_ENDPOINTS.DISTRICTS.LIST}?${queryString}` : API_ENDPOINTS.DISTRICTS.LIST;
  
  return get<DistrictsResponse>(endpoint);
};

export const getDistrict = async (districtId: number): Promise<DistrictDetail> => {
  return get<DistrictDetail>(API_ENDPOINTS.DISTRICTS.UPDATE(districtId.toString()));
};

export const createDistrict = async (data: DistrictCreate): Promise<District> => {
  return post<District>(API_ENDPOINTS.DISTRICTS.CREATE, data);
};

export const updateDistrict = async (districtId: number, data: DistrictUpdate): Promise<District> => {
  return put<District>(API_ENDPOINTS.DISTRICTS.UPDATE(districtId.toString()), data);
};

export const getDataSharingConsent = (
  districtId: number
): Promise<DataSharingConsent> =>
  get<DataSharingConsent>(
    API_ENDPOINTS.DISTRICTS.DATA_SHARING_CONSENT(districtId.toString())
  );

export const updateDataSharingConsent = (
  districtId: number,
  data: DataSharingConsentUpdate
): Promise<DataSharingConsent> =>
  put<DataSharingConsent>(
    API_ENDPOINTS.DISTRICTS.DATA_SHARING_CONSENT(districtId.toString()),
    data
  );

export interface AiScoringAssistSettings {
  district_id: number;
  enabled: boolean;
  updated_by_user_id: number | null;
  updated_at: string | null;
}

export interface AiScoringAssistSettingsUpdate {
  enabled: boolean;
}

export const getAiScoringAssistSettings = (
  districtId: number
): Promise<AiScoringAssistSettings> =>
  get<AiScoringAssistSettings>(
    API_ENDPOINTS.DISTRICTS.AI_SCORING_ASSIST(districtId.toString())
  );

export const updateAiScoringAssistSettings = (
  districtId: number,
  data: AiScoringAssistSettingsUpdate
): Promise<AiScoringAssistSettings> =>
  put<AiScoringAssistSettings>(
    API_ENDPOINTS.DISTRICTS.AI_SCORING_ASSIST(districtId.toString()),
    data
  );

export const deleteDistrict = async (districtId: number): Promise<void> => {
  return del<void>(API_ENDPOINTS.DISTRICTS.DELETE(districtId.toString()));
};
